import {
    pxToRem,
    pxArrayToRem,
    getScrollbarStyle,
    checkboxClassName,
    labelClassName,
    buttonClassName,
    checkboxSlotClassNames,
    categorySelectorClassName,
    virtualListSlotClassNames,
    virtualListClassName,
    rangeSelectorClassName,
    rangeSelectorSlotClassNames,
    searchableListSlotClassNames,
    indicatorCheckboxClassName,
    multiSliderSlotClassNames,
    multiSliderClassName,
    rangeInputClassName,
    captionClassName,
    rangeInputSlotClassNames,
    accordionSlotClassNames,
    categorySelectorSlotClassNames,
    accordionTitleCustomSlotClassNames,
    accordionClassName,
    accordionTitleSlotClassNames,
} from '@archipro-design/aria';
import type { StyleRule } from '@archipro-design/aria';

export const ContentContainer: StyleRule = ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    overflowX: 'hidden',
    overflowY: 'overlay',
    padding: pxArrayToRem([44, 18, 100, 18]),
    marginBottom: pxToRem(80),
    ...getScrollbarStyle(theme, '0'),
    '&::-webkit-scrollbar-thumb:vertical': {
        backgroundColor: theme.siteVariables.colors.white[300],
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    '-webkit-appearance': 'none',
    '-webkit-box-orient': 'inherit',
    '-webkit-font-smoothing': 'inherit',
    '-webkit-line-clamp': 'none',
    '-webkit-text-fill-color': 'inherit',
    animationName: 'none',

    [`& .${checkboxClassName}`]: {
        height: pxToRem(40),
        [`& .${labelClassName}`]: {
            marginLeft: pxToRem(5),
            fontSize: pxToRem(16),
            lineHeight: 1,
            letterSpacing: theme.siteVariables.letterSpacingNormal,
        },

        [`& .${indicatorCheckboxClassName}`]: {
            margin: pxArrayToRem([2, 1, 2, 2]),
            width: pxToRem(11),
            height: pxToRem(12),
        },
        [`& .${checkboxSlotClassNames.indicatorContainer}`]: {
            alignItems: 'start',
        },

        '&:not(:last-child)': {
            marginBottom: pxToRem(0),
        },
    },

    [`& .${accordionSlotClassNames.content}`]: {
        height: pxToRem(32),
    },

    [`& .${accordionClassName}`]: {
        width: '100%',
        [`& .${accordionSlotClassNames.content}`]: {
            width: '100%',
        },
        [`& .${accordionSlotClassNames.title}`]: {
            width: '100%',
            [`& .${accordionTitleSlotClassNames.contentWrapper}`]: {
                width: '100%',
                overflow: 'hidden',
                [`> :not(.${accordionTitleCustomSlotClassNames.titleIndicator})`]:
                    {
                        display: 'inline-block',
                        overflow: 'hidden',
                        flex: 1,
                    },
            },
        },
    },

    [`& .${categorySelectorSlotClassNames.header}`]: {
        [`& .${buttonClassName}`]: {
            paddingTop: 0,
        },
    },

    '& .filter-by-title': {
        marginBottom: pxToRem(8),
        padding: 0,
        letterSpacing: '-0.03em',
        fontWeight: 500,
    },

    [`& .${categorySelectorClassName}`]: {
        marginBottom: pxToRem(8),
        paddingLeft: pxToRem(1),
        paddingTop: pxToRem(1),
        [`& .${buttonClassName}`]: {
            padding: pxArrayToRem([8, 8, 8, 8]),
            [`& .${labelClassName}`]: {
                fontSize: pxToRem(17),
                letterSpacing: '-0.03em',
            },
        },
        [`& .${categorySelectorSlotClassNames.header}`]: {
            [`& .${buttonClassName}:first-child`]: {
                paddingTop: 0,
            },
        },
    },

    [`& .${categorySelectorSlotClassNames.categoriesContainer}`]: {
        marginTop: pxToRem(2),
        marginLeft: pxToRem(4),
        paddingBottom: pxToRem(8),
    },

    [`& .${virtualListClassName}`]: {
        [`& .${virtualListSlotClassNames.listContainer}`]: {
            '&::-webkit-scrollbar-thumb:vertical': {
                backgroundColor: theme.siteVariables.colors.white[300],
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
        },

        [`&.${searchableListSlotClassNames.options}`]: {
            marginTop: pxToRem(20),
            width: pxToRem(380),
            maxHeight: pxToRem(303),

            [`& .${checkboxClassName}`]: {
                [`& .${labelClassName}`]: {
                    marginLeft: pxToRem(4),
                },

                [`& .${indicatorCheckboxClassName}`]: {
                    margin: pxToRem(2),
                    marginLeft: pxToRem(3),
                    width: pxToRem(12),
                    height: pxToRem(12),
                },
            },
        },
    },

    [`& .filter-sizeGroup`]: {
        marginTop: pxToRem(-4),
    },

    [`& .${rangeSelectorClassName}`]: {
        [`& .${rangeSelectorSlotClassNames.title}`]: {
            fontSize: pxToRem(16),
            lineHeight: 1,
        },
        [`& .${rangeSelectorSlotClassNames.inputWrapper}`]: {
            marginTop: pxToRem(11),
            [`& .${rangeInputClassName}`]: {
                height: pxToRem(42),
                padding: pxArrayToRem([0, 12]),
            },
            [`& > .${captionClassName}`]: {
                marginTop: pxToRem(-4),
                fontSize: pxToRem(19),
            },
            [`& .${rangeInputSlotClassNames.unit}`]: {
                lineHeight: `max(16px, ${pxToRem(14)})`,
                fontSize: `max(16px, ${pxToRem(14)})`,
            },
            [`& .${rangeInputSlotClassNames.input} input`]: {
                lineHeight: `max(16px, ${pxToRem(14)})`,
                fontSize: `max(16px, ${pxToRem(14)})`,
                letterSpacing: theme.siteVariables.letterSpacingMedium,
            },

            [`& .${rangeInputSlotClassNames.input} input:disabled`]: {
                opacity: 1,
                color: 'inherit',
            },
        },
        marginBottom: pxToRem(18),
        [`& .${multiSliderClassName}`]: {
            marginTop: pxToRem(13),
            [`& .${multiSliderSlotClassNames.rail}`]: {
                marginTop: pxToRem(8),
            },
            [`& .${multiSliderSlotClassNames.track}`]: {
                marginTop: pxToRem(8),
            },
            [`& .${multiSliderSlotClassNames.inputWrapper}`]: {
                margin: `0 ${pxToRem(8)}`,
            },
        },
    },

    '& .matrialSearchList': {
        marginTop: pxToRem(-8),
    },

    '& .supplierSearchList': {
        marginTop: pxToRem(-8),
    },

    [`& .${accordionSlotClassNames.content}`]: {
        '& > .filter-pricingGroup': {
            marginBottom: pxToRem(16),
            '& .ui-indicatorCheckbox': {
                marginLeft: pxToRem(2),
                marginRight: pxToRem(1),
            },
            '& > div:not(:last-child)': {
                marginBottom: pxToRem(9),
            },
            marginTop: 0,
        },
    },

    '& .filter-availabilityGroup': {
        marginBottom: pxToRem(8),
        marginTop: pxToRem(0),
        [`& .${checkboxClassName}`]: {
            height: pxToRem(32),
        },
    },

    [`& .accordion-filters`]: {
        borderBottom: `1px solid ${theme.siteVariables.colors.transparency.black['10%']}`,
        [`& .${accordionSlotClassNames.title}`]: {
            borderTop: `1px solid ${theme.siteVariables.colors.transparency.black['10%']}`,
        },
        [`& .${accordionSlotClassNames.title}:first-child`]: {
            borderTop: 0,
        },
        [`& .${accordionSlotClassNames.content}`]: {
            marginBottom: pxToRem(0),
        },
        [`& .${checkboxClassName}`]: {
            height: pxToRem(32),
        },
    },
});

/**
 * Styles the category accordion title to look like a section heading (e.g., SORT BY, FILTER BY)
 * when rendered without a separate Caption heading (products mobile filter only).
 */
export const CategoryHeadingAccordion: StyleRule = () => ({
    paddingBottom: pxToRem(25),
    '& .title': {
        textTransform: 'uppercase',
        fontWeight: 500,
        letterSpacing: '-0.03em',
    },
});

/**
 * Hides the root breadcrumb (e.g., "< Product") in the category selector
 * while keeping deeper breadcrumbs (e.g., "< Bathroom") visible.
 * Uses adjacent sibling combinator: backButton is always followed by the root headerCategory.
 */
export const HideRootBreadcrumb: StyleRule = () => ({
    [`& .${categorySelectorSlotClassNames.backButton} + .${categorySelectorSlotClassNames.headerCategory}`]:
        {
            display: 'none',
        },
});

export const SortByContainer: StyleRule = () => ({
    padding: pxArrayToRem([0, 0, 25, 0]),

    '& .sort-by-title': {
        marginBottom: pxToRem(8),
        padding: 0,
        letterSpacing: '-0.03em',
        fontWeight: 500,
    },

    [`& .${checkboxClassName}`]: {
        [`& .${checkboxSlotClassNames.indicatorContainer}`]: {
            alignItems: 'start',
        },
    },
});
